import { useState, useMemo } from "react";
import { useAIAgents } from "@/hooks/useAIAgents";
import { AgentsList } from "@/components/security/AgentsList";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, Bot, Search } from "lucide-react";
import { useTenant } from "@/providers/TenantProvider";

export function AgentsListPage() {
  const [search, setSearch] = useState("");
  const [days, setDays] = useState(30);
  const { selectedOrgId } = useTenant();

  const { data: agents, isLoading, error } = useAIAgents(days, selectedOrgId);

  const filteredAgents = useMemo(() => {
    if (!agents) return [];
    if (!search.trim()) return agents;
    const query = search.toLowerCase();
    return agents.filter(
      (a) =>
        a.name.toLowerCase().includes(query) ||
        (a.description && a.description.toLowerCase().includes(query)) ||
        a.modelName.toLowerCase().includes(query)
    );
  }, [agents, search]);

  if (isLoading) return (
    <div className="flex flex-1 flex-col bg-muted/10">
      <div className="flex flex-col gap-6 px-4 py-6 lg:px-6">
        <div className="space-y-2"><Skeleton className="h-7 w-36" /><Skeleton className="h-4 w-64" /></div>
        <div className="flex gap-3"><Skeleton className="h-10 w-80 rounded-[50px]" /><Skeleton className="h-10 w-24 rounded-[50px]" /></div>
        <Skeleton className="h-[360px] rounded-[15px]" />
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="flex flex-1 flex-col bg-muted/10 p-4 lg:p-6">
        <Card className="rounded-[15px] border-destructive shadow-none">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive">Error</h3>
                <p className="text-sm text-muted-foreground">
                  Failed to load agents. Please try again later.
                </p>
                {error instanceof Error && (
                  <p className="text-xs text-muted-foreground mt-1">{error.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!agents || agents.length === 0) {
    return (
      <div className="flex flex-1 flex-col gap-6 bg-muted/10 px-4 py-6 lg:px-6">
        <div>
          <h1 className="text-xl font-medium">AI Agents</h1>
          <p className="text-sm text-muted-foreground">Monitor and manage your AI agents</p>
        </div>
        <Card className="rounded-[15px] shadow-none"><CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <Bot className="h-12 w-12 mb-4 text-muted-foreground opacity-50" />
          <p className="text-sm font-medium mb-1">No agents found</p>
          <p className="text-xs text-muted-foreground">
            Agents will appear here once they start sending traces.
          </p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col bg-muted/10">
      <div className="flex flex-col gap-6 px-4 py-6 lg:px-6">
      <div>
        <h1 className="text-xl font-medium">AI Agents</h1>
        <p className="text-sm text-muted-foreground">Monitor and manage your AI agents</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search agents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-[50px] bg-white pl-9"
          />
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[100px] rounded-[50px] bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-[20px] bg-white">
            <SelectItem value="7">7d</SelectItem>
            <SelectItem value="30">30d</SelectItem>
            <SelectItem value="90">90d</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredAgents.length === 0 ? (
        <Card className="rounded-[15px] shadow-none"><CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <Search className="h-10 w-10 mb-3 text-muted-foreground opacity-50" />
          <p className="text-sm font-medium mb-1">No agents match your search</p>
          <p className="text-xs text-muted-foreground">Try a different search term.</p>
        </CardContent></Card>
      ) : (
        <AgentsList agents={filteredAgents} />
      )}
      </div>
    </div>
  );
}
